// ============================================================================
// Invoicing — main page (/invoicing). Two stacked sections:
//   1. Ready to invoice — facilities with unbilled hours lines (finance/exec/
//      superuser only; client_billing_lines is RLS-restricted to them).
//   2. Invoices — the draft/sent/paid list (RLS scopes rows per role).
// "New invoice draft" opens the contract-governed, multi-rate draft modal.
// ============================================================================

import { hasFinanceCapability } from '../../shared/role-gates';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, FileText, Plus, Receipt } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { Button, EmptyState, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { fmtDate } from '../../shared/format';

import {
  createMonthlyInvoiceDraft,
  priorCalendarMonth,
  useInvoiceQueue,
  useInvoices,
  useReadyToInvoice,
} from './hooks/use-invoicing-data';
import {
  INVOICE_STATUS_CLASSES,
  INVOICE_STATUS_LABELS,
  fmtHours,
  fmtMoney,
} from './types';
import type { InvoiceRow, InvoiceStatus, QueueFacility, ReadyToInvoiceRow } from './types';
import { CreateDraftModal } from './create-draft-modal';
import { errorMessage } from '../../shared/errors';

export function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        INVOICE_STATUS_CLASSES[status] ?? 'bg-tcs-surface text-tcs-text-secondary'
      }`}
    >
      {INVOICE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function InvoicingQueue() {
  const { profile } = useAuth();
  // Finance-capable = primary finance/exec/superuser OR extra_roles 'finance'
  // hat (Antonela). Mirrors the invoices RLS, now on has_finance_capability().
  const canSeeQueue = hasFinanceCapability(profile);

  const ready = useReadyToInvoice();
  const queue = useInvoiceQueue();
  const invoices = useInvoices();
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [presetFacility, setPresetFacility] = useState<string | null>(null);
  const [genId, setGenId] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<
    { created: number; skipped: number; errors: number; details: string[] } | null
  >(null);

  function openDraft(facilityId?: string) {
    setPresetFacility(facilityId ?? null);
    setModalOpen(true);
  }

  // Monthly-rate facilities generate a draft in one click (prior calendar
  // month). Hourly/contract facilities route to the v2 modal instead.
  function isMonthlyRow(f: ReadyToInvoiceRow): boolean {
    return f.billing_model === 'Monthly' && f.monthly_rate != null && f.monthly_rate > 0;
  }

  async function generateMonthly(f: ReadyToInvoiceRow) {
    const { start, end } = priorCalendarMonth();
    setGenId(f.facility_id);
    try {
      await createMonthlyInvoiceDraft({
        facilityId: f.facility_id,
        periodStart: start,
        periodEnd: end,
        notes: null,
      });
      toast.success(`Draft created for ${f.facility_name ?? f.facility_id}`);
      ready.refetch();
      invoices.refetch();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not generate the draft.'));
    } finally {
      setGenId(null);
    }
  }

  async function generateAllMonthly() {
    const targets = ready.data.filter(isMonthlyRow);
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Generate ${targets.length} monthly draft${targets.length === 1 ? '' : 's'} for the prior calendar month? Each becomes a Draft you can review — nothing is sent.`,
      )
    ) {
      return;
    }
    const { start, end } = priorCalendarMonth();
    setBulkRunning(true);
    setBulkResult(null);
    let created = 0;
    let skipped = 0;
    let errors = 0;
    const details: string[] = [];
    for (const f of targets) {
      try {
        await createMonthlyInvoiceDraft({ facilityId: f.facility_id, periodStart: start, periodEnd: end, notes: null });
        created += 1;
      } catch (err) {
        const msg = errorMessage(err, String(err));
        // Idempotency / missing-rate guards surface as skips, not hard errors.
        if (/already exists|no positive monthly_rate|not Active|no bill-to|nothing to bill/i.test(msg)) {
          skipped += 1;
          details.push(`Skipped ${f.facility_name ?? f.facility_id}: ${msg}`);
        } else {
          errors += 1;
          details.push(`Error ${f.facility_name ?? f.facility_id}: ${msg}`);
        }
      }
    }
    setBulkResult({ created, skipped, errors, details });
    setBulkRunning(false);
    toast.success(`${created} created · ${skipped} skipped · ${errors} errors`);
    ready.refetch();
    invoices.refetch();
  }

  const monthlyReadyCount = ready.data.filter(isMonthlyRow).length;

  const totalUnbilledHours = queue.data.reduce((s, f) => s + f.total_hours, 0);
  const draftCount = invoices.data.filter((i) =>
    ['Draft', 'Pending_Hours_Validation', 'Pending_Finance_Review', 'Approved'].includes(i.status),
  ).length;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <PageHeader
        eyebrow="Finance"
        title="Invoicing"
        meta={
          canSeeQueue
            ? `${ready.data.length} facilities ready to bill · ${queue.data.length} with unbilled hrs · ${draftCount} open drafts`
            : `${invoices.data.length} invoices in your scope`
        }
        actions={
          canSeeQueue ? (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => openDraft()}>
              New invoice draft
            </Button>
          ) : undefined
        }
      />

      {/* ---- Ready to invoice — derived backlog (AFB-0035) ---------------- */}
      {canSeeQueue && (
        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-tcs-text">Ready to invoice</h2>
            {monthlyReadyCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkRunning}
                onClick={generateAllMonthly}
              >
                {bulkRunning ? 'Generating…' : `Generate all monthly drafts (${monthlyReadyCount})`}
              </Button>
            )}
          </div>
          <p className="mb-2 text-xs text-tcs-text-muted">
            Active facilities that have started and aren&apos;t yet billed for the current month.
            Monthly-rate facilities generate a draft in one click; hourly/contract facilities open the
            rate-mapping modal.
          </p>
          {bulkResult && (
            <div className="mb-2 rounded-lg border border-tcs-border bg-tcs-surface p-3 text-xs text-tcs-text">
              <div className="font-semibold">
                {bulkResult.created} created · {bulkResult.skipped} skipped · {bulkResult.errors} errors
              </div>
              {bulkResult.details.length > 0 && (
                <ul className="mt-1 max-h-40 list-disc space-y-0.5 overflow-auto pl-4 text-tcs-text-muted">
                  {bulkResult.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {ready.isLoading && (
            <div className="space-y-2">
              <Skeleton block height={56} width="100%" />
              <Skeleton block height={56} width="100%" />
            </div>
          )}
          {ready.error && (
            <ErrorState title="Couldn't load ready-to-invoice" body={ready.error} onRetry={ready.refetch} />
          )}
          {!ready.isLoading && !ready.error && ready.data.length === 0 && (
            <EmptyState
              icon={<Receipt className="h-12 w-12" strokeWidth={1.5} />}
              title="Nothing ready to invoice"
              body="Active facilities past their start date with no current-month invoice will appear here."
            />
          )}
          <div className="space-y-2">
            {ready.data.map((f) => (
              <ReadyCard
                key={f.facility_id}
                facility={f}
                isMonthly={isMonthlyRow(f)}
                busy={genId === f.facility_id}
                onGenerate={() => generateMonthly(f)}
                onOpenModal={() => openDraft(f.facility_id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ---- Unbilled hour lines (finance/exec/superuser) ----------------- */}
      {canSeeQueue && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-tcs-text">
            Unbilled hour lines
            {queue.data.length > 0 && (
              <span className="ml-2 font-normal text-tcs-text-muted">
                {fmtHours(totalUnbilledHours)} hrs across {queue.data.length}
              </span>
            )}
          </h2>
          {queue.isLoading && (
            <div className="space-y-2">
              <Skeleton block height={64} width="100%" />
              <Skeleton block height={64} width="100%" />
            </div>
          )}
          {queue.error && <ErrorState title="Couldn't load the queue" body={queue.error} onRetry={queue.refetch} />}
          {!queue.isLoading && !queue.error && queue.data.length === 0 && (
            <EmptyState
              icon={<Receipt className="h-12 w-12" strokeWidth={1.5} />}
              title="Nothing waiting to be invoiced"
              body="Unbilled hour-report lines will show up here grouped by facility."
            />
          )}
          <div className="space-y-2">
            {queue.data.map((f) => (
              <QueueCard key={f.facility_id} facility={f} onCreate={() => openDraft(f.facility_id)} />
            ))}
          </div>
        </section>
      )}

      {/* ---- Invoices list ------------------------------------------------ */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-tcs-text">Invoices</h2>
        {invoices.isLoading && (
          <div className="space-y-2">
            <Skeleton block height={48} width="100%" />
            <Skeleton block height={48} width="100%" />
          </div>
        )}
        {invoices.error && (
          <ErrorState title="Couldn't load invoices" body={invoices.error} onRetry={invoices.refetch} />
        )}
        {!invoices.isLoading && !invoices.error && invoices.data.length === 0 && (
          <EmptyState
            icon={<FileText className="h-12 w-12" strokeWidth={1.5} />}
            title="No invoices yet"
            body={canSeeQueue ? 'Create a draft from the queue above to get started.' : 'Invoices for your facilities will appear here.'}
          />
        )}
        <div className="divide-y divide-tcs-border overflow-hidden rounded-lg border border-tcs-border bg-tcs-bg">
          {invoices.data.map((inv) => (
            <InvoiceRowItem key={inv.invoice_id} invoice={inv} />
          ))}
        </div>
      </section>

      {modalOpen && (
        <CreateDraftModal
          facilities={queue.data}
          initialFacilityId={presetFacility}
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            queue.refetch();
            invoices.refetch();
          }}
        />
      )}
    </div>
  );
}

function ReadyCard({
  facility,
  isMonthly,
  busy,
  onGenerate,
  onOpenModal,
}: {
  facility: ReadyToInvoiceRow;
  isMonthly: boolean;
  busy: boolean;
  onGenerate: () => void;
  onOpenModal: () => void;
}) {
  // Facility name + main cleaner/owner in parens (AM naming rule).
  const title = facility.main_cleaner_name
    ? `${facility.facility_name ?? facility.facility_id} (${facility.main_cleaner_name})`
    : facility.facility_name ?? facility.facility_id;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-tcs-border bg-tcs-bg p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-tcs-text">{title}</span>
          {facility.billing_model && (
            <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tcs-text-muted">
              {facility.billing_model}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-tcs-text-muted">
          {facility.bill_to_account_name ? `Bill to ${facility.bill_to_account_name} · ` : ''}
          {facility.monthly_rate != null ? `${fmtMoney(facility.monthly_rate)}/mo · ` : ''}
          Started {fmtDate(facility.effective_start)}
        </div>
        <div className="mt-0.5 text-[11px] text-tcs-text-muted">
          {facility.last_invoiced_period_end
            ? `Last invoiced through ${fmtDate(facility.last_invoiced_period_end)}`
            : 'Never invoiced'}
        </div>
      </div>
      {isMonthly ? (
        <Button size="sm" disabled={busy} onClick={onGenerate}>
          {busy ? 'Generating…' : 'Generate draft'}
        </Button>
      ) : (
        <Button size="sm" variant="secondary" onClick={onOpenModal}>
          Create draft
        </Button>
      )}
    </div>
  );
}

function QueueCard({ facility, onCreate }: { facility: QueueFacility; onCreate: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-tcs-border bg-tcs-bg p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-tcs-text">{facility.facility_name}</span>
          {facility.facility_type && (
            <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tcs-text-muted">
              {facility.facility_type}
            </span>
          )}
          {!facility.has_active_contract && (
            <span className="inline-flex items-center gap-1 rounded bg-st-warn-bg-strong px-1.5 py-0.5 text-[10px] font-semibold text-st-warn-fg-strong">
              <AlertTriangle className="h-3 w-3" /> Needs contract
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-tcs-text-muted">
          {facility.account_name ? `${facility.account_name} · ` : ''}
          {facility.depts.map((d) => `${d.dept} (${fmtHours(d.hours)}h)`).join(' · ')}
        </div>
        <div className="mt-0.5 text-[11px] text-tcs-text-muted">
          {fmtDate(facility.earliest_week)} – {fmtDate(facility.latest_week)} · {facility.total_lines} lines · {fmtHours(facility.total_hours)} hrs
        </div>
      </div>
      <Button
        size="sm"
        variant={facility.has_active_contract ? 'primary' : 'secondary'}
        onClick={onCreate}
      >
        Create draft
      </Button>
    </div>
  );
}

function InvoiceRowItem({ invoice }: { invoice: InvoiceRow }) {
  return (
    <Link
      to={`/invoicing/${invoice.invoice_id}`}
      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-tcs-bg-muted"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-tcs-text-secondary">
            {invoice.invoice_number ?? invoice.invoice_id}
          </span>
          <InvoiceStatusBadge status={invoice.status} />
        </div>
        <div className="mt-0.5 truncate text-sm text-tcs-text">
          {invoice.facilities?.facility_name ?? invoice.accounts?.account_name ?? invoice.account_id}
        </div>
        <div className="text-[11px] text-tcs-text-muted">
          {fmtDate(invoice.billing_period_start)} – {fmtDate(invoice.billing_period_end)} · {invoice.billing_model}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm font-semibold text-tcs-text">{fmtMoney(invoice.total_amount)}</div>
        {invoice.total_hours != null && (
          <div className="text-[11px] text-tcs-text-muted">{fmtHours(invoice.total_hours)} hrs</div>
        )}
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-tcs-text-muted" />
    </Link>
  );
}
